import { useMemo } from 'react'
import Big from 'big.js'
import { DEFAULT_BASE_CURRENCY } from '../../DonationForm/constants'
import { DonationType, IStreamerConfig } from '@/pages/Donation/api/streamer/types'
import { useStreamerConfigContext } from '@/contexts/StreamerConfigContext/StreamerConfigContext'
import { CustomizeType } from '@/pages/Donation/types'
import { useRate } from '@/pages/Donation/useRate'
import { convert } from '@/pages/Donation/utils'

const DEFAULT_MIN_AMOUNT = 5

type Props = {
  baseCurrencyCode?: string
  customizeType: CustomizeType
  donationType: DonationType
}

const getCustomizationAmount = (customizeType: CustomizeType, videoMinAmount: string, imageMinAmount: string) => {
  if (customizeType?.includes('image') && customizeType?.includes('video')) {
    return Number(videoMinAmount) > Number(imageMinAmount) ? videoMinAmount : imageMinAmount
  } else if (customizeType?.includes('video')) {
    return videoMinAmount
  } else if (customizeType?.includes('image')) {
    return imageMinAmount
  }

  return '0'
}

const getStreamerMinAmount = (
  config: IStreamerConfig | null,
  donationType: DonationType,
  customizeType: CustomizeType
) => {
  if (!config) {
    return
  }

  const textMinAmount = config.text_donation_config?.min_amount || '0'
  const imageMinAmount = config.image_donation_config?.min_amount || '0'
  const videoMinAmount = config.video_donation_config?.min_amount || '0'
  const audioMinAmount = config.audio_donation_config?.min_amount || '0'

  const customizationAmount = getCustomizationAmount(customizeType, videoMinAmount, imageMinAmount)

  if (donationType === DonationType.Audio) {
    return Number(customizationAmount) > Number(audioMinAmount) ? customizationAmount : audioMinAmount
  }

  if (donationType === DonationType.Text) {
    return Number(customizationAmount) > Number(textMinAmount) ? customizationAmount : textMinAmount
  }
}

export const useMinAmount = ({ baseCurrencyCode, customizeType, donationType }: Props) => {
  const { config } = useStreamerConfigContext()
  const streamerCurrency = config?.base_currency || DEFAULT_BASE_CURRENCY
  const streamerMinAmount = getStreamerMinAmount(config, donationType, customizeType)

  const { rate: defaultBaseToBaseRate, isLoading: isBaseRateLoading } = useRate({
    fromCode: DEFAULT_BASE_CURRENCY,
    toCode: baseCurrencyCode,
  })
  const { rate: streamerToBaseRate, isLoading: isStreamerRateLoading } = useRate({
    fromCode: streamerCurrency,
    toCode: baseCurrencyCode,
  })

  const minAmount = useMemo(() => {
    let value
    if (streamerMinAmount) {
      value =
        streamerToBaseRate != null
          ? convert({ amount: streamerMinAmount, rate: streamerToBaseRate })
          : streamerMinAmount
    } else {
      value =
        defaultBaseToBaseRate != null
          ? convert({ amount: DEFAULT_MIN_AMOUNT, rate: defaultBaseToBaseRate })
          : DEFAULT_MIN_AMOUNT
    }

    return new Big(value ?? 0).round(2, 3).toNumber()
  }, [defaultBaseToBaseRate, streamerMinAmount, streamerToBaseRate])

  return {
    minAmount,
    isLoading: isBaseRateLoading || isStreamerRateLoading,
  }
}
